import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, Plus, X } from 'lucide-react';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import { SheetField, SheetRow, SheetSection } from '../settings/sheet';

/**
 * «Διανομείς» στις ρυθμίσεις Delivery (απόφαση ιδιοκτήτη 12/10/2026): ο διανομέας δεν έχει λογαριασμό —
 * υπάρχει μόνο για την ανάθεση (αποστολή, Ζ, analytics). Όνομα + «Ενεργός», τίποτα άλλο: ούτε PIN, ούτε
 * email, ούτε τηλέφωνο. Ποτέ διαγραφή (οι παραγγελίες τον κρατούν)· ο ανενεργός δεν φαίνεται στην αποστολή.
 * Από 13/10/2026 μία ενότητα του «Φύλλου» (settings/sheet): «+ Διανομέας» κάτω από την εξήγηση.
 *
 * API: GET/POST /api/delivery/:storeId/drivers, PATCH …/drivers/:id (routes/delivery.js, μόνο διαχειριστής).
 */

export interface DeliveryDriver {
  id: number;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';
type Draft = { first_name: string; last_name: string };
type Result = { ok: boolean; status: number; data: any };

const EMPTY: Draft = { first_name: '', last_name: '' };

const fullName = (d: Pick<DeliveryDriver, 'first_name' | 'last_name'>) => `${d.first_name || ''} ${d.last_name || ''}`.trim();

// Ίδια σειρά με τον server: ενεργοί πρώτα, μετά αλφαβητικά.
const sortDrivers = (list: DeliveryDriver[]) => [...list].sort((a, b) =>
  Number(b.is_active) - Number(a.is_active) || fullName(a).localeCompare(fullName(b), 'el') || a.id - b.id);

async function request(method: string, url: string, body?: unknown): Promise<Result> {
  try {
    const token = localStorage.getItem('xentrix_token');
    const res = await fetch(url, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

// Όπως το INPUT_CLS των ρυθμίσεων (settings/storeSettingsForm) — χωρίς να φέρνει μαζί το useAuth.
const INPUT_CLS = 'w-full px-3.5 py-2.5 border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring text-sm';
const ghostBtn = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong '
  + 'bg-surface-2 text-t1 hover:bg-surface-3 transition disabled:opacity-50';
const primaryBtn = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-semibold bg-brand text-brand-fg '
  + 'hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed';
// Ίδιο με το κουμπί «Προσθήκη σειράς» της αρίθμησης παραστατικών (κάτω από την εξήγηση της ενότητας).
const asideBtn = 'inline-flex items-center gap-1 min-h-[36px] px-3 rounded-lg border border-line-strong text-t2 hover:bg-surface-3 '
  + 'text-sm font-medium transition-colors disabled:opacity-50';
// Σε στενό φύλλο (data-layout του SettingsSheet) τα πεδία μπαίνουν το ένα κάτω από το άλλο.
const NARROW_STACK = 'group-data-[layout=narrow]/sheet:flex-col group-data-[layout=narrow]/sheet:items-stretch';

interface NameFormProps {
  initial: Draft;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (draft: Draft) => void;
  onCancel: () => void;
  onChange: () => void;
}

/** Όνομα + επώνυμο (προαιρετικό) — για νέο διανομέα και για μετονομασία. */
const NameForm: React.FC<NameFormProps> = ({ initial, busy, error, submitLabel, onSubmit, onCancel, onChange }) => {
  const { t } = useTranslation(['delivery']);
  const uid = useId();
  const [draft, setDraft] = useState<Draft>(initial);
  const set = (patch: Partial<Draft>) => { onChange(); setDraft(d => ({ ...d, ...patch })); };
  return (
    <form
      className="py-1"
      onSubmit={e => { e.preventDefault(); onSubmit(draft); }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}
      noValidate
    >
      <div className={`flex items-end gap-3 ${NARROW_STACK}`}>
        <SheetField label={t('delivery:driversSettings.firstName')} htmlFor={`${uid}-first`} className="flex-1">
          <input id={`${uid}-first`} autoFocus value={draft.first_name} autoComplete="off" maxLength={100}
            onChange={e => set({ first_name: e.target.value })} aria-invalid={!!error} className={INPUT_CLS} />
        </SheetField>
        <SheetField
          label={<>{t('delivery:driversSettings.lastName')}&nbsp;<span className="font-normal text-t4">({t('delivery:driversSettings.optional')})</span></>}
          htmlFor={`${uid}-last`}
          className="flex-1"
        >
          <input id={`${uid}-last`} value={draft.last_name} autoComplete="off" maxLength={100}
            onChange={e => set({ last_name: e.target.value })} className={INPUT_CLS} />
        </SheetField>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={onCancel} disabled={busy} className={ghostBtn}>
            <X className="w-4 h-4" aria-hidden="true" />{t('delivery:driversSettings.cancel')}
          </button>
          <button type="submit" disabled={busy} aria-busy={busy} className={primaryBtn}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Check className="w-4 h-4" aria-hidden="true" />}
            {submitLabel}
          </button>
        </div>
      </div>
      {error && <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </form>
  );
};

interface Props {
  storeId: number;
}

const DriversSection: React.FC<Props> = ({ storeId }) => {
  const { t } = useTranslation(['delivery', 'common']);
  const { showToast } = useToast();
  const [drivers, setDrivers] = useState<DeliveryDriver[]>([]);
  const [load, setLoad] = useState<LoadState>('loading');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | 'new' | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const base = `${API_BASE_URL}/api/delivery/${storeId}/drivers`;

  const fetchDrivers = useCallback(async () => {
    setLoad('loading');
    const r = await request('GET', base);
    if (r.status === 401 || r.status === 403) { setLoad('forbidden'); return; }
    if (!r.ok || !Array.isArray(r.data)) { setLoad('error'); return; }
    setDrivers(sortDrivers(r.data as DeliveryDriver[]));
    setLoad('ready');
  }, [base]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Μήνυμα αποτυχίας: ο κωδικός του server όπου τον ξέρουμε, αλλιώς γενικό.
  const failure = (r: Result) => {
    if (r.status === 403) return t('delivery:driversSettings.forbidden');
    if (r.data?.code === 'FIRST_NAME_REQUIRED') return t('delivery:driversSettings.firstNameRequired');
    if (r.status === 404) return t('delivery:driversSettings.notFound');
    return t('delivery:driversSettings.saveFailed');
  };

  const replace = (driver: DeliveryDriver) => setDrivers(list => sortDrivers(list.map(d => (d.id === driver.id ? driver : d))));

  const create = async (draft: Draft) => {
    if (!draft.first_name.trim()) { setFormError(t('delivery:driversSettings.firstNameRequired')); return; }
    setBusy('new');
    const r = await request('POST', base, { first_name: draft.first_name.trim(), last_name: draft.last_name.trim() });
    setBusy(null);
    if (!r.ok || !r.data?.driver) { setFormError(failure(r)); return; }
    const driver = r.data.driver as DeliveryDriver;
    setDrivers(list => sortDrivers([...list, driver]));
    setAdding(false);
    setFormError(null);
    showToast('success', t('delivery:driversSettings.added', { name: fullName(driver) }));
  };

  const rename = async (driver: DeliveryDriver, draft: Draft) => {
    const first = draft.first_name.trim();
    const last = draft.last_name.trim();
    if (!first) { setFormError(t('delivery:driversSettings.firstNameRequired')); return; }
    // Μόνο ό,τι άλλαξε· χωρίς αλλαγή απλώς κλείνει.
    const body: Partial<Draft> = {};
    if (first !== driver.first_name) body.first_name = first;
    if (last !== (driver.last_name || '')) body.last_name = last;
    if (Object.keys(body).length === 0) { setEditingId(null); setFormError(null); return; }
    setBusy(driver.id);
    const r = await request('PATCH', `${base}/${driver.id}`, body);
    setBusy(null);
    if (!r.ok || !r.data?.driver) { setFormError(failure(r)); return; }
    replace(r.data.driver as DeliveryDriver);
    setEditingId(null);
    setFormError(null);
  };

  // Ο διακόπτης αλλάζει μόνο αφού το δεχτεί ο server· σε αποτυχία μένει όπως ήταν και λέμε γιατί.
  const toggle = async (driver: DeliveryDriver) => {
    setBusy(driver.id);
    const r = await request('PATCH', `${base}/${driver.id}`, { is_active: !driver.is_active });
    setBusy(null);
    if (!r.ok || !r.data?.driver) { showToast('error', failure(r)); return; }
    replace(r.data.driver as DeliveryDriver);
  };

  const openAdd = () => { setEditingId(null); setFormError(null); setAdding(true); };
  const openRename = (id: number) => { setAdding(false); setFormError(null); setEditingId(id); };
  const closeForms = () => { setAdding(false); setEditingId(null); setFormError(null); };

  const row = (d: DeliveryDriver) => {
    const name = fullName(d) || '—';
    return (
      <li key={d.id} data-testid={`driver-row-${d.id}`}>
        {editingId === d.id ? (
          <NameForm initial={{ first_name: d.first_name || '', last_name: d.last_name || '' }} busy={busy === d.id} error={formError}
            submitLabel={t('delivery:driversSettings.save')} onSubmit={draft => rename(d, draft)} onCancel={closeForms}
            onChange={() => setFormError(null)} />
        ) : (
          <SheetRow
            title={<span className={`min-w-0 truncate ${d.is_active ? '' : 'text-t3'}`}>{name}</span>}
            subtitle={d.is_active ? t('delivery:driversSettings.active') : t('delivery:driversSettings.inactive')}
            control={(
              <span className="flex items-center gap-2">
                <button type="button" onClick={() => openRename(d.id)} disabled={busy !== null}
                  aria-label={t('delivery:driversSettings.renameAria', { name })}
                  className="w-10 h-10 -my-2 rounded-xl text-t3 hover:text-t1 hover:bg-surface-3 flex items-center justify-center disabled:opacity-50">
                  <Pencil className="w-4 h-4" aria-hidden="true" />
                </button>
                {/* Όχι SettingsSwitch: το όνομα του διακόπτη λέει και ποιον αφορά («Ενεργός: …»). */}
                <button type="button" role="switch" aria-checked={d.is_active} disabled={busy !== null}
                  aria-label={t('delivery:driversSettings.activeAria', { name })} onClick={() => toggle(d)}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2 disabled:opacity-50 disabled:cursor-not-allowed ${d.is_active ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${d.is_active ? 'translate-x-5' : ''}`} />
                </button>
              </span>
            )}
          />
        )}
      </li>
    );
  };

  const hasInactive = drivers.some(d => !d.is_active);

  return (
    <SheetSection
      id="delivery-drivers"
      title={t('delivery:driversSettings.title')}
      hint={t('delivery:driversSettings.subtitle')}
      testId="drivers-section"
      aside={(
        <button type="button" onClick={openAdd} disabled={load !== 'ready' || adding || busy !== null} className={asideBtn}>
          <Plus className="w-4 h-4" aria-hidden="true" />
          {t('delivery:driversSettings.add')}
        </button>
      )}
    >
      {load === 'loading' && (
        <div className="flex items-center gap-2 py-2 text-sm text-t3" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />{t('delivery:driversSettings.loading')}
        </div>
      )}
      {(load === 'error' || load === 'forbidden') && (
        <div className="flex flex-wrap items-center gap-3" role="alert">
          <p className="text-sm text-t2">{load === 'forbidden' ? t('delivery:driversSettings.forbidden') : t('delivery:driversSettings.loadError')}</p>
          {load === 'error' && (
            <button type="button" onClick={fetchDrivers} className={ghostBtn}>{t('delivery:driversSettings.retry')}</button>
          )}
        </div>
      )}
      {load === 'ready' && (
        <>
          {adding && (
            <div className={drivers.length > 0 ? 'pb-5 mb-5 border-b border-line' : ''}>
              <NameForm initial={EMPTY} busy={busy === 'new'} error={formError} submitLabel={t('delivery:driversSettings.create')}
                onSubmit={create} onCancel={closeForms} onChange={() => setFormError(null)} />
            </div>
          )}
          {drivers.length === 0 ? (
            !adding && <p className="text-sm text-t3">{t('delivery:driversSettings.empty')}</p>
          ) : (
            // Λεπτή γραμμή ανάμεσα, όπως ανάμεσα σε διαδοχικές γραμμές του φύλλου (sheet.css .xs-row + .xs-row).
            <ul className="[&>li+li]:mt-4 [&>li+li]:pt-4 [&>li+li]:border-t [&>li+li]:border-line">
              {drivers.map(row)}
            </ul>
          )}
          {hasInactive && <p className="xs-note">{t('delivery:driversSettings.inactiveHint')}</p>}
        </>
      )}
    </SheetSection>
  );
};

export default DriversSection;
